import React from 'react';
import { FaSnowflake } from 'react-icons/fa';
import { FiThermometer, FiDroplet, FiWifi, FiShield } from 'react-icons/fi';

const SensorInfoSummary = () => {
  return (
    <div className="w-full bg-white border border-gray-100 rounded-2xl p-5 font-sans flex flex-col xl:flex-row items-start xl:items-center justify-between gap-6 shadow-[0_2px_12px_rgba(0,0,0,0.01)] select-none">
      
      {/* Left Block: Thumbnail and Main Name/Metadata Tags */}
      <div className="flex items-start gap-4 w-full xl:w-auto">
        {/* Placeholder image representation matching the metal cold room door */}
        <div className="w-24 h-20 rounded-xl overflow-hidden bg-gray-200 border border-gray-100 shrink-0 shadow-xs">
          <img 
            src="/assets/image_6bc906b1.png" 
            alt="Walk-in Chiller" 
            className="w-full h-full object-cover"
          />
        </div>

        {/* Text descriptions and pill badges layout */}
        <div className="space-y-2 flex-1">
          {/* Title Row with Blue Snowflake Icon */}
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center shrink-0">
              <FaSnowflake className="w-3.5 h-3.5" />
            </div>
            <h2 className="text-[18px] font-extrabold text-gray-900 tracking-tight leading-none">
              Walk-in Chiller (North)
            </h2>
          </div>

          {/* Subtext breadcrumb indicators */}
          <div className="flex flex-wrap items-center gap-1.5 text-[12px] font-semibold text-gray-400">
            <span>Kitchen A</span>
            <span className="text-gray-300">•</span>
            <span>Walk-in Unit</span>
            <span className="text-gray-300">•</span>
            <span className="text-gray-400 uppercase">CH-01</span>
          </div>

          {/* Decorative tag pills row */}
          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100/50">
              In Range
            </span>
            <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 border border-purple-100/50">
              HACCP Critical
            </span>
            <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100/50">
              Food Storage
            </span>
          </div>
        </div>
      </div>

      {/* Right Grid: Core Data Point Metric Boxes */}
      <div className="grid grid-cols-2 md:grid-cols-4 items-center gap-4 w-full xl:w-auto flex-1 xl:flex-none xl:pl-6">
        
        {/* Metric 1: Current Temperature Box */}
        <div className="bg-white border border-gray-100/80 rounded-xl p-3 px-4 min-w-[140px] flex flex-col justify-between h-[105px] shadow-[0_1px_4px_rgba(0,0,0,0.01)]">
          <div className="flex items-start justify-between">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tight block">Current Temp</span>
            <div className="w-7 h-7 bg-blue-50/50 text-blue-500 rounded-lg border border-blue-50 flex items-center justify-center shrink-0">
              <FiThermometer className="w-4 h-4" />
            </div>
          </div>
          <div className="space-y-0.5 mt-2">
            <p className="text-[22px] font-extrabold text-gray-900 leading-none">2.4°C</p>
            <span className="text-[10px] font-bold text-gray-400 block whitespace-nowrap">Ideal: 0°C to 5°C</span>
          </div>
        </div>

        {/* Metric 2: Humidity Box (Not Measured) */}
        <div className="bg-white border border-gray-100/80 rounded-xl p-3 px-4 min-w-[140px] flex flex-col justify-between h-[105px] shadow-[0_1px_4px_rgba(0,0,0,0.01)]">
          <div className="flex items-start justify-between">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tight block">Humidity</span>
            <div className="w-7 h-7 bg-blue-50/50 text-blue-500 rounded-lg border border-blue-50 flex items-center justify-center shrink-0">
              <FiDroplet className="w-4 h-4" />
            </div>
          </div>
          <div className="space-y-0.5 mt-2">
            <p className="text-[22px] font-extrabold text-gray-400 leading-none">—</p>
            <span className="text-[10px] font-bold text-gray-400 block whitespace-nowrap">Not Measured</span>
          </div>
        </div>

        {/* Metric 3: Sensor Status Box */}
        <div className="bg-white border border-gray-100/80 rounded-xl p-3 px-4 min-w-[140px] flex flex-col justify-between h-[105px] shadow-[0_1px_4px_rgba(0,0,0,0.01)]">
          <div className="flex items-start justify-between">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tight block">Sensor Status</span>
            <div className="w-7 h-7 bg-emerald-50/50 text-emerald-500 rounded-lg border border-emerald-50 flex items-center justify-center shrink-0">
              <FiWifi className="w-4 h-4 stroke-[2.5]" />
            </div>
          </div>
          <div className="space-y-0.5 mt-2">
            <p className="text-[16px] font-extrabold text-emerald-600 leading-none">Online</p>
            <span className="text-[10px] font-bold text-gray-400 block whitespace-nowrap">18 sec ago</span>
          </div>
        </div>

        {/* Metric 4: Equipment Health Score Box */}
        <div className="bg-white border border-gray-100/80 rounded-xl p-3 px-4 min-w-[140px] flex flex-col justify-between h-[105px] shadow-[0_1px_4px_rgba(0,0,0,0.01)]">
          <div className="flex items-start justify-between">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tight block">Equipment Health</span>
            <div className="w-7 h-7 bg-emerald-50/50 text-emerald-500 rounded-lg border border-emerald-50 flex items-center justify-center shrink-0">
              <FiShield className="w-4 h-4 fill-emerald-500/10" />
            </div>
          </div>
          <div className="space-y-0.5 mt-2">
            <p className="text-[16px] font-extrabold text-gray-900 leading-none">
              <span className="text-emerald-600 text-[20px]">96</span>
              <span className="text-gray-300 text-[13px] font-bold mx-0.5">/</span>
              <span className="text-gray-400 text-[12px] font-bold">100</span>
            </p>
            <span className="text-[10px] font-bold text-gray-400 block whitespace-nowrap">Excellent</span>
          </div>
        </div>

      </div>

    </div>
  );
};

export default SensorInfoSummary;
